import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { env } from '../env.ts';
import { LANGUAGES, rememberLanguage } from '../i18n/index.ts';
import { activePack, whoAmI } from '../lib/data.ts';
import { operatorClient } from '../lib/supabase.ts';

export function Banner() {
  const { t } = useTranslation();
  return env.environment === 'demo' ? (
    <div className="banner" role="status">
      {t('banner.demo')}
    </div>
  ) : null;
}

export function LanguageSwitch() {
  const { i18n } = useTranslation();
  return (
    <select aria-label="language" value={i18n.language} onChange={(e) => rememberLanguage(e.target.value)}>
      {LANGUAGES.map((l) => (
        <option key={l} value={l}>
          {l.toUpperCase()}
        </option>
      ))}
    </select>
  );
}

/** Title of a screen, what it is for, and who works on it. */
export function PageHeader({
  title,
  page,
  children,
}: {
  title: React.ReactNode;
  page: string;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        <p className="lead">{t(`page.${page}.lead`)}</p>
        <p className="who">{t(`page.${page}.who`)}</p>
      </div>
      {children && <div className="page-actions">{children}</div>}
    </header>
  );
}

// The menu: each screen with one line on what it is for. Grouped by the work it supports.
const MENU: { group: string; items: { to: string; key: string; demoOnly?: boolean }[] }[] = [
  {
    group: 'data',
    items: [
      { to: '/ingest', key: 'ingest' },
      { to: '/values', key: 'values' },
      { to: '/evidence', key: 'evidence' },
    ],
  },
  {
    group: 'compliance',
    items: [
      { to: '/batches', key: 'batches' },
      { to: '/worklist', key: 'worklist' },
    ],
  },
  { group: 'help', items: [{ to: '/guide', key: 'guide' }] },
  { group: 'demo', items: [{ to: '/admin', key: 'admin', demoOnly: true }] },
];

// Demo walk-through (PRD v1.0 §13), on the demo dataset's batches: Spirulina PB-2026-0002, Chlorella PB-2026-0007,
// broken chain PB-2026-0004.
const TOUR: { step: number; to: string }[] = [
  { step: 1, to: '/ingest' },
  { step: 2, to: '/batches/PB-2026-0007/genealogy' },
  { step: 3, to: '/batches/PB-2026-0002' },
  { step: 4, to: '/batches/PB-2026-0007/compare' },
  { step: 5, to: '/batches/PB-2026-0007' },
];

function Tour() {
  const { t } = useTranslation();
  return (
    <details className="tour" open>
      <summary>{t('steps.title')}</summary>
      <ol>
        {TOUR.map(({ step, to }) => (
          <li key={step}>
            <NavLink to={to} end title={t(`steps.${step}`)}>
              {t(`tour.${step}`)}
            </NavLink>
          </li>
        ))}
      </ol>
    </details>
  );
}

export function Layout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [pack, setPack] = useState('');
  const [who, setWho] = useState<{ name: string; org: string | null; roles: string[] } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    void activePack()
      .then((p) =>
        setPack(
          p ? `${p.pack.rules.meta.overlay_applied ?? ''} · ${p.pack.route_model.route_model_version}` : '',
        ),
      )
      .catch(() => setPack(''));
    void whoAmI()
      .then(setWho)
      .catch(() => setWho(null));
  }, []);
  async function signOut() {
    await operatorClient().auth.signOut();
    void navigate('/');
  }
  return (
    <div className={`shell${menuOpen ? ' menu-open' : ''}`}>
      <aside className="sidebar">
        <div className="brand">
          <span className="mark" aria-hidden="true">
            W2G
          </span>
          <span>
            <strong>{t('app.short')}</strong>
            <span className="small">{t('app.subtitle')}</span>
          </span>
        </div>
        <nav aria-label={t('nav.menu')}>
          {MENU.map(({ group, items }) => {
            const shown = items.filter((i) => !i.demoOnly || env.environment === 'demo');
            if (shown.length === 0) return null;
            return (
              <div key={group} className="nav-group">
                <p className="nav-group-title">{t(`navgroup.${group}`)}</p>
                {shown.map((i) => (
                  <NavLink
                    key={i.to}
                    to={i.to}
                    className="nav-item"
                    aria-label={t(`nav.${i.key}`)}
                    aria-describedby={`nav-desc-${i.key}`}
                  >
                    <span className="nav-title">{t(`nav.${i.key}`)}</span>
                    <span className="nav-desc" id={`nav-desc-${i.key}`}>
                      {t(`navdesc.${i.key}`)}
                    </span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        {env.environment === 'demo' && <Tour />}
        <div className="sidebar-foot">
          {who && (
            <div className="persona">
              <strong>{who.name}</strong>
              <span className="small">
                {who.org ? `${who.org} · ` : ''}
                {who.roles.map((r) => t(`role.${r}`)).join(', ')}
              </span>
            </div>
          )}
          <div className="foot-actions">
            <LanguageSwitch />
            <button className="ghost" onClick={() => void signOut()}>
              {t('nav.signout')}
            </button>
          </div>
        </div>
      </aside>
      <div className="workspace">
        <div className="topbar">
          <button
            className="ghost menu-toggle"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {t('nav.menu')}
          </button>
          <Banner />
        </div>
        <Outlet />
        <footer className="small muted">
          {env.environment} · {t('footer.version', { engine: __ENGINE_VERSION__, pack })}
        </footer>
      </div>
    </div>
  );
}
